import { fetchInitConfig, fetchKlines, type BrokerSymbol, type KLinePoint, type Period } from '~/api/wikitrade-market'

import { WS_URL, type SocketStatus } from './constants'
import { formatSymbolName } from './utils'

// WebSocket token 生成成本不高，但缓存后可以避免切换周期或刷新订阅时重复走 crypto。
interface StoredWsToken {
  cacheKey: string
  token: string
}

// @wiki/kline-vue 拉历史数据时只需要关心品种、周期和向前分页游标。
interface GetBarsParams {
  symbol: string
  resolution: string
  beforeTime?: number
  countBack?: number
}

// hasMoreBefore/nextBeforeTime 用来驱动图表继续向左加载更早的 K 线。
interface GetBarsResult {
  bars: KLinePoint[]
  hasMoreBefore: boolean
  nextBeforeTime?: number
}

// 实时订阅只绑定当前工作台展示的 symbol + resolution，切换任一项都会重新订阅。
interface SubscribeBarsParams {
  symbol: string
  resolution: string
}

// 组件侧会保存订阅 ID；unsubscribe 函数用于组件生命周期内的即时清理。
interface DatafeedSubscriptionResult {
  id: string
  unsubscribe: () => void
}

// 这里是 K 线组件消费的品种元信息，不完全等同于接口返回的 BrokerSymbol。
interface DatafeedSymbolInfo {
  symbol: string
  name: string
  timezone: string
  pricePrecision: number
  supportedResolutions: string[]
  session?: {
    timezone: string
    regular: string
  }
  calendar?: {
    timezone: string
    weekend: number[]
  }
}

// datafeed 是页面和 K 线工作台之间的适配协议，隐藏接口鉴权、历史分页和实时订阅细节。
export interface WikiTradeDatafeedAdapter {
  resolveSymbol: (symbol: string) => Promise<DatafeedSymbolInfo>
  getBars: (params: GetBarsParams) => Promise<GetBarsResult>
  subscribeBars: (params: SubscribeBarsParams, onBar: (bar: KLinePoint) => void) => Promise<DatafeedSubscriptionResult>
  unsubscribeBars: (subscriberId: string) => Promise<void>
  destroy: () => void
}

// 页面通过回调接收 datafeed 内部状态，这样 datafeed 不直接依赖 Vue 响应式对象。
export interface WikiTradeDatafeedAdapterOptions {
  baseURL: string
  languageCode?: string
  getAccessToken: () => string
  getWsToken?: () => string
  onRealtimeUpdate?: (bar: KLinePoint) => void
  onSocketStatusChange?: (status: SocketStatus) => void
  onLoadingStatusChange?: (loading: boolean) => void
  onDataReplaced?: (bars: KLinePoint[]) => void
  onError?: (error: unknown) => void
}

const WS_TOKEN_STORAGE_KEY = 'wikitrade.websocket.token.v2'
const WS_TOKEN_TTL_MS = 30 * 60 * 1000

const canUseStorage = () => typeof window !== 'undefined' && Boolean(window.localStorage)

// token 缓存只在浏览器可用；SSR 阶段直接返回空，避免访问 localStorage 报错。
const readLocalCache = <T>(key: string, options: { ttlMs?: number } = {}): T | null => {
  if (!canUseStorage()) return null

  try {
    const text = window.localStorage.getItem(key)
    if (!text) return null
    const payload = JSON.parse(text) as { value: T; expiresAt?: number; createdAt?: number }
    const expiresAt = payload.expiresAt || (payload.createdAt && options.ttlMs ? payload.createdAt + options.ttlMs : 0)

    if (expiresAt && expiresAt <= Date.now()) {
      window.localStorage.removeItem(key)
      return null
    }

    return payload.value
  } catch {
    window.localStorage.removeItem(key)
    return null
  }
}

// 写入时同时保存创建时间和过期时间，兼容早期只有 createdAt + ttlMs 的缓存结构。
const writeLocalCache = <T>(key: string, value: T, options: { ttlMs?: number } = {}) => {
  if (!canUseStorage()) return

  const now = Date.now()
  window.localStorage.setItem(
    key,
    JSON.stringify({
      value,
      createdAt: now,
      expiresAt: options.ttlMs ? now + options.ttlMs : undefined,
    }),
  )
}

// 实时报文里数值字段来源较杂，先收敛成可解析文本，再统一转 number。
const toNumericText = (value: unknown) => {
  if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
    return String(value)
  }

  return ''
}

const parseNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(toNumericText(value).replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : Number.NaN
}

const normalizeTimestamp = (timestamp: number) => {
  return timestamp > 0 && timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp
}

// WebSocket 实时报文来源不稳定，这里集中做字段兼容，避免页面层关心报文形态。
// 只返回当前订阅 symbol/period 的完整 OHLC 数据，心跳、鉴权提示和缺字段消息都会被忽略。
const readRealtimePoint = (message: unknown, symbol: string, period: Period): KLinePoint | null => {
  const root = message as any
  const candidates = [root?.data?.k, root?.data, root?.payload?.body, root?.kline, root?.tick, root].filter(Boolean)
  const source = candidates.find((item) => item.timestamp || item.t)

  if (!source) return null
  if (source.symbol && source.symbol !== symbol) return null
  if (source.period && source.period !== period) return null

  const rawTime = parseNumber(source.timestamp ?? source.t)
  const time = Number.isFinite(rawTime) ? normalizeTimestamp(rawTime) : rawTime
  const open = parseNumber(source.open ?? source.open_price ?? source.o)
  const high = parseNumber(source.high ?? source.high_price ?? source.h)
  const low = parseNumber(source.low ?? source.low_price ?? source.l)
  const close = parseNumber(source.close ?? source.close_price ?? source.c)
  const volume = parseNumber(
    source.volume ??
      source.vol ??
      source.v ??
      source.tick_volume ??
      source.trade_volume ??
      source.turnover_volume ??
      source.volume_value,
  )

  if (![time, open, high, low, close].every(Number.isFinite)) return null

  return {
    time,
    open,
    high,
    low,
    close,
    ...(Number.isFinite(volume) ? { volume } : {}),
  }
}

// 交易时段来自品种配置，需要转换成 K 线组件使用的 session/calendar 结构。
// 周日接口用 7 表示，组件沿用 JS Date 的 0 表示周日，因此这里做一次映射。
const parseTransactionTimeToSession = (transactionTimeConfig: {
  timezone: string
  transaction_time_week: Array<{
    week_id: number
    list: Array<{ start_time: string; end_time: string }>
  }>
}) => {
  const { timezone: rawTimezone, transaction_time_week: weekList } = transactionTimeConfig
  let timezone = 'Etc/UTC'

  if (rawTimezone === '+00:00' || rawTimezone === '+00') {
    timezone = 'Etc/UTC'
  } else if (rawTimezone === '+08:00' || rawTimezone === '+08') {
    timezone = 'Asia/Shanghai'
  } else {
    const matched = rawTimezone.match(/^([+-])(\d{2}):?(\d{2})?$/)
    if (matched) {
      const sign = matched[1]
      const hours = Number.parseInt(matched[2] || '0', 10)
      timezone = `Etc/GMT${sign === '+' ? '-' : '+'}${hours}`
    }
  }

  const activeWeekdays = new Set<number>()
  const sessionsByDay: Record<number, string[]> = {}

  if (Array.isArray(weekList)) {
    weekList.forEach((dayItem) => {
      const weekday = dayItem.week_id === 7 ? 0 : dayItem.week_id
      const list = dayItem.list || []

      if (list.length > 0) {
        activeWeekdays.add(weekday)
        sessionsByDay[weekday] = list.map((range) => {
          const start = range.start_time.replace(/:/g, '').slice(0, 4)
          const rawEnd = range.end_time.replace(/:/g, '').slice(0, 4)
          return `${start}-${rawEnd === '2359' ? '2400' : rawEnd}`
        })
      }
    })
  }

  const weekend: number[] = []
  for (let index = 0; index <= 6; index += 1) {
    if (!activeWeekdays.has(index)) weekend.push(index)
  }

  const firstActiveDay = Object.values(sessionsByDay)[0]
  const regularSession = firstActiveDay?.length ? firstActiveDay.join(',') : '0000-2400'

  return {
    timezone,
    session: {
      timezone,
      regular: regularSession,
    },
    calendar: {
      timezone,
      weekend,
    },
  }
}

// 行情 WebSocket 要求用 AES-CBC 生成 URL-safe token；缓存 30 分钟即可覆盖多数页面会话。
export const encryptSocketToken = async (pid: string, accountId: string) => {
  const cacheKey = `${pid}:${accountId}`
  const cached = readLocalCache<StoredWsToken>(WS_TOKEN_STORAGE_KEY, { ttlMs: WS_TOKEN_TTL_MS })
  if (cached?.cacheKey === cacheKey && cached.token) return cached.token

  // 兜底逻辑：在客户端非安全上下文（如局域网 HTTP 开发调试）下，Web Crypto API (crypto.subtle) 不可用。
  // 针对默认的 pid=502 且 accountId 为空的情况，直接使用预计算的加密 token 结果，避免报错。
  const isDefaultConfig = pid === '502' && !accountId
  const hasSubtleCrypto = typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined'

  if (!hasSubtleCrypto && isDefaultConfig) {
    const token = 'dzVNENEZIr3jmHBj4_1sbA'
    writeLocalCache(WS_TOKEN_STORAGE_KEY, { cacheKey, token }, { ttlMs: WS_TOKEN_TTL_MS })
    return token
  }

  const text = `${pid}@${accountId}@0`
  const keyStr = '1234567890123456'
  const ivStr = 'I Love Go Frame!'
  const encoder = new TextEncoder()
  const data = encoder.encode(text)
  const keyData = encoder.encode(keyStr)
  const ivData = encoder.encode(ivStr)

  try {
    const cryptoKey = await crypto.subtle.importKey('raw', keyData, { name: 'AES-CBC' }, false, ['encrypt'])
    const encryptedBuffer = await crypto.subtle.encrypt({ name: 'AES-CBC', iv: ivData }, cryptoKey, data)
    const base64 = btoa(String.fromCharCode(...new Uint8Array(encryptedBuffer)))
    const token = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

    writeLocalCache(WS_TOKEN_STORAGE_KEY, { cacheKey, token }, { ttlMs: WS_TOKEN_TTL_MS })
    return token
  } catch (error) {
    if (!hasSubtleCrypto) {
      throw new Error(
        'Web Crypto API (crypto.subtle) is not supported in this environment (likely due to insecure HTTP context).',
        { cause: error },
      )
    }
    throw error
  }
}

// 适配 @wiki/kline-vue 所需的 datafeed 协议，内部串联历史接口和 WebSocket 实时订阅。
export const createWikiTradeDatafeedAdapter = (options: WikiTradeDatafeedAdapterOptions): WikiTradeDatafeedAdapter => {
  let socket: WebSocket | null = null
  let socketOpenPromise: Promise<void> | null = null
  let heartbeatId: number | undefined
  let reconnectTimerId: number | undefined
  let closeSocketTimerId: number | undefined
  let stabilityTimerId: number | undefined
  let reconnectAttempt = 0
  let destroyed = false
  let lastRequestId = 0
  let currentSubscription: { symbol: string; resolution: string } | null = null
  const subscribers = new Map<string, (bar: KLinePoint) => void>()

  // 订阅协议由行情服务定义，affair_id/ack_id 使用当前时间生成即可满足一次请求的唯一性。
  const sendSubscription = (operation: 'subscribe' | 'unsubscribe', symbol: string, resolution: string) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return

    const now = Date.now()
    socket.send(
      JSON.stringify({
        type: 'request',
        affair_id: String(now),
        ack_id: String(now + 1),
        payload: {
          operation,
          type: 'kline',
          body: {
            content: { symbol, period: resolution },
          },
        },
      }),
    )
  }

  const stopHeartbeat = () => {
    if (!heartbeatId) return
    window.clearInterval(heartbeatId)
    heartbeatId = undefined
  }

  const clearReconnectTimer = () => {
    if (!reconnectTimerId) return
    window.clearTimeout(reconnectTimerId)
    reconnectTimerId = undefined
  }

  const cancelCloseSocketTimer = () => {
    if (!closeSocketTimerId) return
    window.clearTimeout(closeSocketTimerId)
    closeSocketTimerId = undefined
  }

  const closeSocket = () => {
    cancelCloseSocketTimer()
    clearReconnectTimer()
    stopHeartbeat()

    if (stabilityTimerId) {
      window.clearTimeout(stabilityTimerId)
      stabilityTimerId = undefined
    }

    if (socket) {
      socket.close()
      socket = null
      socketOpenPromise = null
    }
  }

  // 只有存在有效订阅和监听者时才重连，避免页面切换后后台继续拉起连接。
  const scheduleReconnect = () => {
    if (destroyed || !currentSubscription || subscribers.size === 0 || reconnectTimerId) return

    const delay = Math.min(15_000, 1000 * 2 ** reconnectAttempt)
    reconnectAttempt += 1
    reconnectTimerId = window.setTimeout(() => {
      reconnectTimerId = undefined
      void ensureSocket(true)
    }, delay)
  }

  // 取消最后一个订阅后延迟关闭连接，给快速切换周期/品种留一个复用窗口。
  const scheduleCloseSocket = () => {
    cancelCloseSocketTimer()
    closeSocketTimerId = window.setTimeout(() => {
      closeSocketTimerId = undefined
      closeSocket()
    }, 5000)
  }

  // 服务端需要定期心跳保持连接，payload 内容本身不参与业务解析。
  const startHeartbeat = () => {
    stopHeartbeat()
    heartbeatId = window.setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(
          JSON.stringify({
            type: 'heart',
            ack_id: Math.random().toString(36).slice(2, 15),
            payload: {
              ping: new Date().toLocaleString(),
            },
          }),
        )
      }
    }, 10_000)
  }

  // 统一管理连接复用、强制重连和重连后的自动补订阅。
  const ensureSocket = async (forceReconnect = false) => {
    if (destroyed) return

    const wsToken = options.getWsToken?.()
    if (!forceReconnect && socket?.readyState === WebSocket.OPEN) return

    if (!forceReconnect && socket?.readyState === WebSocket.CONNECTING) {
      await socketOpenPromise
      return
    }

    if (forceReconnect && socket) {
      socket.close()
      socket = null
    }

    clearReconnectTimer()
    options.onSocketStatusChange?.('connecting')
    const ws = new WebSocket(wsToken ? `${WS_URL}?token=${wsToken}` : WS_URL)
    socket = ws
    socketOpenPromise = new Promise((resolve) => {
      ws.addEventListener('open', () => socket === ws && resolve(), { once: true })
      ws.addEventListener('close', () => resolve(), { once: true })
      ws.addEventListener('error', () => resolve(), { once: true })
    })

    ws.addEventListener('open', () => {
      if (socket !== ws) return

      socketOpenPromise = null
      options.onSocketStatusChange?.('live')
      startHeartbeat()

      if (stabilityTimerId) window.clearTimeout(stabilityTimerId)
      stabilityTimerId = window.setTimeout(() => {
        stabilityTimerId = undefined
        if (socket === ws) reconnectAttempt = 0
      }, 60_000)

      if (currentSubscription) {
        sendSubscription('subscribe', currentSubscription.symbol, currentSubscription.resolution)
      }
    })

    ws.addEventListener('message', (event) => {
      if (socket !== ws || !event.data || !currentSubscription) return

      try {
        const message = JSON.parse(String(event.data))
        const bar = readRealtimePoint(message, currentSubscription.symbol, currentSubscription.resolution as Period)
        if (!bar) return

        subscribers.forEach((callback) => callback(bar))
        options.onRealtimeUpdate?.(bar)
      } catch {
        // 非 JSON 消息（心跳、鉴权提示等纯文本）直接忽略。
      }
    })

    ws.addEventListener('error', () => {
      if (socket !== ws) return
      socketOpenPromise = null
      options.onSocketStatusChange?.('error')
    })

    ws.addEventListener('close', () => {
      if (socket !== ws) return

      socket = null
      socketOpenPromise = null
      options.onSocketStatusChange?.('closed')
      stopHeartbeat()

      if (stabilityTimerId) {
        window.clearTimeout(stabilityTimerId)
        stabilityTimerId = undefined
      }

      scheduleReconnect()
    })

    await socketOpenPromise
  }

  return {
    // 图表首次加载或切换品种时会调用，用接口配置补齐名称、精度和交易时段。
    async resolveSymbol(symbol: string) {
      const accessToken = options.getAccessToken()
      if (!accessToken) throw new Error('Access token not available')

      const symbols = await fetchInitConfig(options.baseURL, accessToken, {
        languageCode: options.languageCode,
      })
      const brokerSymbol = symbols.find((item: BrokerSymbol) => item.symbol === symbol)
      const transactionTimeData = (brokerSymbol as Record<string, any> | undefined)?.transaction_time
      const sessionConfig =
        transactionTimeData &&
        typeof transactionTimeData === 'object' &&
        Array.isArray(transactionTimeData.transaction_time_week)
          ? parseTransactionTimeToSession(transactionTimeData)
          : {}

      return {
        symbol: brokerSymbol?.symbol || symbol,
        name: brokerSymbol ? formatSymbolName(brokerSymbol) : symbol,
        timezone: 'Asia/Shanghai',
        pricePrecision: brokerSymbol?.price_precision ?? 2,
        supportedResolutions: ['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w', '1M'],
        ...sessionConfig,
      }
    },
    // 历史 K 线按 beforeTime 向前分页；首次加载会通过 onDataReplaced 同步页面状态。
    async getBars(params: GetBarsParams) {
      const requestId = ++lastRequestId
      const accessToken = options.getAccessToken()

      if (!accessToken) {
        return { bars: [], hasMoreBefore: false }
      }

      try {
        options.onLoadingStatusChange?.(true)
        const fetchedBars = await fetchKlines(
          options.baseURL,
          params.symbol,
          params.resolution as Period,
          accessToken,
          {
            beforeTime: params.beforeTime,
            limit: params.countBack,
            languageCode: options.languageCode,
          },
        )
        const hasBeforeTime = Number.isFinite(params.beforeTime)
        const bars = hasBeforeTime ? fetchedBars.filter((bar) => bar.time < (params.beforeTime as number)) : fetchedBars
        const nextBeforeTime = bars[0]?.time
        const cursorMoved =
          !hasBeforeTime ||
          (Number.isFinite(nextBeforeTime) && (nextBeforeTime as number) < (params.beforeTime as number))
        const requestedCount =
          Number.isFinite(params.countBack) && (params.countBack as number) > 0
            ? Math.ceil(params.countBack as number)
            : undefined

        if (requestId === lastRequestId) {
          if (!params.beforeTime) {
            options.onDataReplaced?.(bars)
          }
          options.onLoadingStatusChange?.(false)
        }

        return {
          bars,
          hasMoreBefore: Boolean(cursorMoved && requestedCount !== undefined && bars.length >= requestedCount),
          nextBeforeTime,
        }
      } catch (error) {
        if (requestId === lastRequestId) {
          options.onLoadingStatusChange?.(false)
          options.onError?.(error)
        }
        throw error
      }
    },
    // K 线组件可以多次订阅同一个品种周期，这里用 Map 保存回调并共享同一条 WS 连接。
    async subscribeBars(params: SubscribeBarsParams, onBar: (bar: KLinePoint) => void) {
      const subId = `${params.symbol}:${params.resolution}`
      subscribers.set(subId, onBar)
      currentSubscription = {
        symbol: params.symbol,
        resolution: params.resolution,
      }

      cancelCloseSocketTimer()
      const canSendImmediately = socket?.readyState === WebSocket.OPEN
      await ensureSocket()
      if (canSendImmediately) {
        sendSubscription('subscribe', params.symbol, params.resolution)
      }

      return {
        id: subId,
        unsubscribe: () => {
          subscribers.delete(subId)
          sendSubscription('unsubscribe', params.symbol, params.resolution)

          if (currentSubscription?.symbol === params.symbol && currentSubscription?.resolution === params.resolution) {
            currentSubscription = null
          }

          if (subscribers.size === 0) {
            scheduleCloseSocket()
          }
        },
      }
    },
    // 组件只传订阅 ID 时走这个出口；真正的 WS 关闭会等到没有任何订阅者之后再延迟执行。
    async unsubscribeBars(subscriberId: string) {
      subscribers.delete(subscriberId)

      if (subscribers.size === 0) {
        currentSubscription = null
        scheduleCloseSocket()
      }
    },
    // 页面卸载或 access token 变化时必须彻底清理，防止旧连接继续推送到新页面状态。
    destroy() {
      destroyed = true
      closeSocket()
      subscribers.clear()
      currentSubscription = null
    },
  }
}
